import { z } from 'zod';
import { daysBetweenInclusive } from '../dates';
import { CLAIM_PERIOD_STATUSES, EDITABLE_ITC_STATUSES, ITC_STATUSES } from '../enums';
import { emptyAsUndefined, id, listQuery, optionalText, requiredId, ymd } from './common';

const ordered = (r: { startDate: string; endDate: string }) => r.startDate <= r.endDate;

/** GET /gst/summary — GST totals, by month and by GST account. */
export const gstSummaryQuerySchema = z
  .object({ startDate: ymd, endDate: ymd, gstAccountId: id.optional() })
  .refine(ordered, { path: ['endDate'], message: 'End date must be on or after start date' })
  .refine((r) => !ordered(r) || daysBetweenInclusive(r.startDate, r.endDate) <= 1100, {
    path: ['endDate'],
    message: 'Range cannot exceed 1100 days',
  });

export const GST_SOURCES = ['MARKETING', 'OTHER'] as const;
export type GstSource = (typeof GST_SOURCES)[number];

/** GET /gst/transactions — marketing and other-expense GST rows in one list. */
export const gstTransactionsQuerySchema = listQuery.extend({
  startDate: ymd.optional(),
  endDate: ymd.optional(),
  gstAccountId: id.optional(),
  /** Rows without a GST account. */
  unassigned: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  itcStatus: z.enum(ITC_STATUSES).optional(),
  source: z.enum(GST_SOURCES).optional(),
  claimPeriodId: id.optional(),
});
export type GstTransactionsQuery = z.output<typeof gstTransactionsQuerySchema>;

/** POST /gst/itc-status — change the ITC status of selected rows (audited). */
export const bulkItcStatusSchema = z.object({
  items: z
    .array(z.object({ source: z.enum(GST_SOURCES), id }))
    .min(1, 'Select at least one row')
    .max(500),
  itcStatus: z.enum(EDITABLE_ITC_STATUSES),
  /** For CLAIMED: date and reference of the return (GSTR-3B). */
  itcClaimDate: emptyAsUndefined(ymd),
  claimReference: optionalText(100),
});
export type BulkItcStatusInput = z.output<typeof bulkItcStatusSchema>;

/** Put selected GST rows on a GST account (e.g. entered before the account had one). */
export const bulkGstAccountSchema = z.object({
  items: z
    .array(z.object({ source: z.enum(GST_SOURCES), id }))
    .min(1, 'Select at least one row')
    .max(500),
  gstAccountId: requiredId('GST account'),
});
export type BulkGstAccountInput = z.output<typeof bulkGstAccountSchema>;

/** Give ads spend without a GST account the GST account its ads account has now. */
export const fillGstAccountsSchema = z.object({
  startDate: ymd.optional(),
  endDate: ymd.optional(),
});

// ── Claim periods ───────────────────────────────────────────────────

export const claimListQuerySchema = z.object({
  gstAccountId: id.optional(),
  status: z.enum(CLAIM_PERIOD_STATUSES).optional(),
  year: z.coerce.number().int().min(2000).max(2100).optional(),
});

export const claimCreateSchema = z.object({
  gstAccountId: id,
  year: z.coerce.number().int().min(2000).max(2100),
  month: z.coerce.number().int().min(1).max(12),
  notes: optionalText(2000),
});
export type ClaimCreateInput = z.output<typeof claimCreateSchema>;

export const CLAIM_ACTIONS = ['submit', 'claim', 'close', 'reopen'] as const;
export type ClaimAction = (typeof CLAIM_ACTIONS)[number];

export const claimActionSchema = z.object({
  action: z.enum(CLAIM_ACTIONS),
  /** For "claim": date the ITC was claimed (default today) and the return reference. */
  claimDate: emptyAsUndefined(ymd),
  claimReference: optionalText(100),
  notes: optionalText(2000),
  updatedAt: z.iso.datetime({ offset: true }).optional(),
});
export type ClaimActionInput = z.output<typeof claimActionSchema>;

// ── Attachments ─────────────────────────────────────────────────────

export const ATTACHMENT_ENTITIES = [
  'MarketingExpense',
  'OtherExpense',
  /** Verification documents of a Google Play developer account (administrators only). */
  'PlayDeveloperAccount',
] as const;
export type AttachmentEntity = (typeof ATTACHMENT_ENTITIES)[number];
export const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
export const ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

export const attachmentTargetSchema = z.object({
  entityType: z.enum(ATTACHMENT_ENTITIES),
  entityId: id,
});

// ── Exports ─────────────────────────────────────────────────────────

export const EXPORT_REPORTS = [
  'pnl',
  'app-performance',
  'marketing-report',
  'revenue-report',
  'marketing-expenses',
  'revenue-entries',
  'other-expenses',
  'gst-summary',
  'gst-transactions',
] as const;
export type ExportReport = (typeof EXPORT_REPORTS)[number];
export const exportFormatSchema = z.object({ format: z.enum(['csv', 'xlsx']).default('xlsx') });
